import { parseISO, differenceInDays } from "date-fns";
import L from "leaflet";
import * as GeometryUtil from "leaflet-geometryutil";
import { union } from "lodash";
import get from "lodash/get";
import has from "lodash/has";
import head from "lodash/head";
import keys from "lodash/keys";
import mapValues from "lodash/mapValues";
import sortBy from "lodash/sortBy";
import upperFirst from "lodash/upperFirst";
import values from "lodash/values";

import { getToday, isOnSeason } from "./seasons";
import {
  Unit,
  SportFilter,
  StatusFilter,
  UnitConnection,
  SkiingFilter,
  HikingFilter,
  SeasonDelimiter,
} from "./types";
import {
  DEFAULT_STATUS_FILTER,
  QualityEnum,
  Seasons,
  UNIT_HANDLE_HEIGHT,
  UNIT_PIN_HEIGHT,
  UnitFilters,
  UnitQuality,
  SportFilters,
  SkiingFilters,
  UnitAutomaticConditionChangeDays,
  UnitConnectionTags,
  UnitQualityConst,
} from "./unitConstants";
import { DEFAULT_LANG } from "../app/appConstants";
import { AppState } from "../app/types";
import i18n from "../i18n/i18n";
import {
  IceSkatingServices,
  SkiingServices,
  UnitServices,
  SupportingServices,
} from "../service/serviceConstants";
import { getServicesForSport, getServicesBySport, getOnSeasonSportFilters, getOnSeasonHikeFilters } from "../service/serviceHelpers";

export const getAttr = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  attr: Record<string, any>,
  lang: string = DEFAULT_LANG,
) => {
  let translated = has(attr, lang) && attr[lang];

  if (!translated) {
    for (let i = 0; i < keys(attr).length; ++i) {
      translated = attr[keys(attr)[i]];

      if (translated) {
        break;
      }
    }
  }

  return translated || null;
};

export const getUnitPosition = (unit: Unit): [number, number] | undefined => {
  // If the unit doesn't have set location but has a geometry, eg. ski track,
  // use the first point in the geometry.
  if (
    !unit.location &&
    unit.geometry.type === "MultiLineString" &&
    unit.geometry.coordinates
  ) {
    const [long, lat] = unit.geometry.coordinates[0][0];

    return [lat, long];
  }

  if (unit?.location?.coordinates) {
    const [long, lat] = unit.location.coordinates;

    return [lat, long];
  }
};

export const createReittiopasUrl = (unit: Unit, lang: string) => {
  const lat = get(unit, "location.coordinates[1]");
  const lon = get(unit, "location.coordinates[0]");
  const origin = " "; // sic
  const street = getAttr(unit.street_address, lang);
  const municipality = unit.municipality || "";
  const coordinates = lat && lon ? encodeURIComponent(`::${lat},${lon}`) : "";

  const to = encodeURIComponent(
    `${upperFirst(street)}, ${upperFirst(municipality)}${coordinates}`,
  );

  const from = encodeURIComponent(origin);
  const url = `https://reittiopas.hsl.fi/reitti/${from}/${to}`;

  return url;
};

export const createPalvelukarttaUrl = (unit: Unit, lang: string) =>
  `https://palvelukartta.hel.fi/${lang}/unit/${unit.id}`;

export const getUnitSport = (unit: Unit) => {
  if (unit.services && unit.services.length) {
    const servicesBySport = getServicesBySport();
    
    // Check each sport's services to see if any match the unit's services
    for (const [sport, sportServices] of Object.entries(servicesBySport)) {
      if (unit.services.some(service => sportServices.includes(service))) {
        // Handle special cases for supporting services
        if (sport === UnitFilters.HIKING) {
          // Return specific service type for supporting services
          for (const service of unit.services) {
            switch (service) {
              case UnitServices.COOKING_FACILITY:
                return UnitFilters.COOKING_FACILITY;
              case UnitServices.CAMPING:
                return UnitFilters.CAMPING;
              case UnitServices.SKI_LODGE:
                return UnitFilters.SKI_LODGE;
              case UnitServices.INFORMATION_POINT:
                return UnitFilters.INFORMATION_POINT;
              case UnitServices.LEAN_TO:
                return UnitFilters.LEAN_TO;
            }
          }
        }
        return sport as SportFilter;
      }
    }
  }

  return "unknown";
};

export const getObservation = (unit: Unit, matchProperty: string) => {
  const { observations } = unit;

  return observations
    ? observations.find((obs) => obs.property.includes(matchProperty))
    : null;
};

export const getCondition = (unit: Unit) => {
  // Safely check if unit and observations exist before destructuring
  if (!unit?.observations) {
    return null;
  }
  
  const { observations } = unit;
  return observations.find((obs) => obs.primary) || null;
};

export const getUnitQuality = (unit: Unit): string => {
  const observation = getCondition(unit);

  return observation ? observation.quality : UnitQuality.UNKNOWN;
};

export const getOpeningHours = (unit: Unit, activeLang: string): string[] => {
  const isMechanicallyFrozenIce = unit.services.includes(
    UnitServices.MECHANICALLY_FROZEN_ICE,
  );

  if (!isMechanicallyFrozenIce) {
    return [];
  }

  const connections = unit.connections || [];

  return connections
    .filter((connection) => connection.section_type === "OPENING_HOURS")
    .map((connection) => getAttr(connection.name, activeLang) || "")
    .filter((result) => result !== "");
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getObservationTime = (observation: Record<string, any>) => {
  const timeValue = observation?.time || 0;
  
  if (timeValue === 0) {
    return new Date(0); // Return epoch date for falsy values
  }
  
  // Handle ISO string format
  if (typeof timeValue === 'string') {
    try {
      return parseISO(timeValue);
    } catch {
      return new Date('Invalid Date');
    }
  }
  
  // Handle numeric timestamp
  if (typeof timeValue === 'number') {
    return new Date(timeValue);
  }
  
  return new Date('Invalid Date');
};

export const enumerableQuality = (quality: string): number =>
  QualityEnum[quality] ? QualityEnum[quality] : Number.MAX_VALUE;

export const getConnectionByTag = (
  unit: Unit,
  tag: string,
): UnitConnection | undefined => {
  return unit.connections.find(
    (connection) => connection.tags && connection.tags.includes(tag),
  );
};

/**
 * ICONS
 */
export const getUnitIconURL = (
  unit: Unit,
  selected: boolean | null | undefined = false,
  retina: boolean | null | undefined = true,
) => {
  // Use safe quality assessment with fallback for units without observations
  const quality = unit?.observations ? getUnitQuality(unit) : 'unknown';
  const sport = getUnitSport(unit);
  const onOff = selected ? "on" : "off";
  const resolution = retina ? "@2x" : "";

  // Reference assets from public directory
  return `/assets/markers/${sport}-${quality}-${onOff}${resolution}.png`;
};

export const getUnitIconHeight = (unit: Unit) =>
  getUnitSport(unit) === UnitFilters.SKIING
    ? UNIT_HANDLE_HEIGHT
    : UNIT_PIN_HEIGHT;

export const getUnitIcon = (
  unit: Unit,
  selected: boolean | null | undefined = false,
) => ({
  url: getUnitIconURL(unit, selected, false),
  retinaUrl: getUnitIconURL(unit, selected, true),
  height: getUnitIconHeight(unit),
});

export const getFilterIconURL = (
  filter: string,
) =>
  filter ? new URL(`../assets/icons/icon-white-${filter}@2x.png`, import.meta.url).href : "";

/**
 * FILTERZ
 */
// These functions are now centralized in serviceHelpers.ts
// Keeping these exports for backward compatibility
export { getOnSeasonSportFilters, getOnSeasonHikeFilters } from "../service/serviceHelpers";

export const getOffSeasonSportFilters = (
  date: SeasonDelimiter = getToday(),
): Array<string> =>
  Seasons.filter((season) => !isOnSeason(date, season))
    .map(({ filters }) => filters)
    .reduce((flattened, filters) => [...flattened, ...filters], []);

export const getSportFilters = (date: SeasonDelimiter = getToday()) => ({
  onSeason: getOnSeasonSportFilters(date),
  offSeason: getOffSeasonSportFilters(date),
});

export const getDefaultSportFilter = (): SportFilter =>
  head(getOnSeasonSportFilters(getToday())) || SportFilters[0];

export const getDefaultStatusFilter = (): StatusFilter => DEFAULT_STATUS_FILTER;

export const getDefaultFilters = () => ({
  status: getDefaultStatusFilter(),
  sport: getDefaultSportFilter(),
});

export const getSportSpecificationFilters = (
  sport: SportFilter,
): SkiingFilter[] | HikingFilter[] => {
  if (sport === UnitFilters.SKIING) {
    return [...SkiingFilters];
  }

  if (sport === UnitFilters.HIKING) {
    const seasonHikeFilters = getOnSeasonHikeFilters();
    return seasonHikeFilters;
  }
  return [];
};

/**
 * SORT UNIT LIST
 */
const _sortByDistance = (
  units: Unit[],
  position: [number, number],
  leafletMap: L.Map,
) => {
  if (leafletMap === null) {
    return units;
  }

  const positionLatLng = new L.LatLng(...position);

  return sortBy(units, (unit) => {
    if (
      unit.geometry === null ||
      unit.geometry === undefined ||
      unit.geometry.type === "Point"
    ) {
      if (unit.location === null || unit.location === undefined) {
        return 0;
      }

      return positionLatLng.distanceTo(
        L.GeoJSON.coordsToLatLng(unit.location.coordinates),
      );
    }

    if (unit.geometry.type === "MultiLineString") {
      const latLngs = L.GeoJSON.coordsToLatLngs(unit.geometry.coordinates, 1);

      const closestLatLng = GeometryUtil.closest(
        leafletMap,
        latLngs,
        positionLatLng,
      );

      return positionLatLng.distanceTo(closestLatLng);
    }
  });
};

export const sortByDistance = _sortByDistance;

export const sortByName = (units: Unit[], lang?: string) =>
  sortBy(units, (unit) => getAttr(unit.name, lang));

export const sortByCondition = (units: Unit[]) =>
  sortBy(units, [
    (unit) => {
      // Ensure unit has required properties for quality assessment
      if (!unit?.observations) {
        return QualityEnum.unknown; // Return lowest priority for units without observations
      }
      return enumerableQuality(getUnitQuality(unit));
    },
    (unit) => {
      // Ensure unit has observations before calling getCondition
      if (!unit?.observations) {
        return Number.MAX_SAFE_INTEGER; // Return maximum time difference for units without observations
      }
      const observation = getCondition(unit);

      const observationTime =
        (observation &&
          observation.time &&
          new Date(observation.time).getTime()) ||
        0;

      return new Date().getTime() - observationTime;
    },
  ]);

export const sortByFavorites = (units: Unit[]): Unit[] => {
  const favourites = JSON.parse(localStorage.getItem("favouriteUnits") || "[]");
  return units.filter((unit) =>
    favourites.some((favourite: Unit) => favourite.id === unit.id),
  );
};

export const getAddressToDisplay = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  address: Record<string, any>,
  activeLang: string,
) => {
  if (Object.keys(address).length === 0) {
    return null;
  }

  const streetName = getAttr(address.street?.name, activeLang) || "";
  const streetAddress = [streetName, address.number].filter(Boolean).join(" ");
  const municipality = getAttr(address.municipality?.name, activeLang);

  return [streetAddress, upperFirst(municipality)].filter(Boolean).join(", ");
};

export const getAllSportServices = () => {
  return values(UnitServices).join(",");
};

export const getOnSeasonSportServices = () => {
  const sportFilters = getOnSeasonSportFilters();
  const primarySport = sportFilters?.[0];
  
  if (primarySport) {
    return getServicesForSport(primarySport).join(",");
  }
  
  // Fallback to winter sports if no specific sport is on season
  return SkiingServices.concat(IceSkatingServices).join(",");
};

export const getNoneHikingUnit = (
  visibleUnits: string[],
  stateUnits: AppState["unit"],
): string[] => {
  // Create array from all the unit objects.
  const visibleUnitObjects: Unit[] = Object.assign(
    [],
    Object.values(stateUnits.byId).filter((u) =>
      visibleUnits.includes(u.id.toString()),
    ),
  );

  return visibleUnitObjects
    .filter((u) => {
      const containsService = u.services.some((item) =>
        SupportingServices.includes(item),
      );
      return !containsService ? u : null;
    })
    .map((u) => u.id.toString());
};

export const getFilteredUnitsBySportSpecification = (
  visibleUnits: string[],
  stateUnits: AppState["unit"],
  sportSpecification: string,
): string[] => {
  // If nothing to filter
  if (!sportSpecification) return visibleUnits;

  // Create array from all the unit objects.
  // Only show the ones that have already been filtered
  const visibleUnitObjects: Unit[] = Object.assign(
    [],
    Object.values(stateUnits.byId).filter((u) =>
      visibleUnits.includes(u.id.toString()),
    ),
  );

  const hasFreestyleFilter: boolean = sportSpecification.includes(
    UnitFilters.SKIING_FREESTYLE,
  );
  const hasTraditionalFilter: boolean = sportSpecification.includes(
    UnitFilters.SKIING_TRADITIONAL,
  );
  const hasDogSkijoringFilter: boolean = sportSpecification.includes(
    UnitFilters.SKIING_DOG_SKIJORING_TRACK,
  );
  const hasLeanToFilter: boolean = sportSpecification.includes(
    UnitFilters.LEAN_TO,
  );
  const hasCookingFilter: boolean = sportSpecification.includes(
    UnitFilters.COOKING_FACILITY,
  );
  const hasCampingFilter: boolean = sportSpecification.includes(
    UnitFilters.CAMPING,
  );
  const hasInfoPointFilter: boolean = sportSpecification.includes(
    UnitFilters.INFORMATION_POINT,
  );
  const hasSkiLodgeFilter: boolean = sportSpecification.includes(
    UnitFilters.SKI_LODGE,
  );
  const skiTrackFreestyleUnits = (): string[] => {
    if (!hasFreestyleFilter) return [];

    // Get list of unit IDs where lipas.skiTrackFreestyle is set to 1
    return visibleUnitObjects
      .filter((u) => {
        return get(u, ["extra", "lipas.skiTrackFreestyle"], null) ? u : null;
      })
      .map((u) => u.id.toString());
  };

  const skiTrackTraditionalUnits = (): string[] => {
    if (!hasTraditionalFilter) return [];

    // Get list of unit IDs where lipas.skiTrackTraditional is set to 1
    return visibleUnitObjects
      .filter((u) => {
        return get(u, ["extra", "lipas.skiTrackTraditional"], null) ? u : null;
      })
      .map((u) => u.id.toString());
  };

  const skiTrackDogSkijoring = (): string[] => {
    if (!hasDogSkijoringFilter) return [];

    // Get list of unit IDs where DOG_SKIJORING_TRACK connection tag is present
    return visibleUnitObjects
      .filter((u) => {
        const connectionsWithDogSkijoring = u.connections.filter((c) =>
          c.tags.includes(UnitConnectionTags.DOG_SKIJORING_TRACK),
        );
        return connectionsWithDogSkijoring.length ? u : null;
      })
      .map((u) => u.id.toString());
  };

  const leanToUnits = (): string[] => {
    if (!hasLeanToFilter) return [];

    // Get list of unit IDs where services includes LeanTo service ids
    return visibleUnitObjects
      .filter((u) => {
        const containsService = u.services.includes(UnitServices.LEAN_TO);
        return containsService ? u : null;
      })
      .map((u) => u.id.toString());
  };

  const CookingFacilityUnits = (): string[] => {
    if (!hasCookingFilter) return [];

    // Get list of unit IDs where services includes cooking facility service ids
    return visibleUnitObjects
      .filter((u) => {
        const containsService = u.services.includes(
          UnitServices.COOKING_FACILITY,
        );
        return containsService ? u : null;
      })
      .map((u) => u.id.toString());
  };

  const CampingUnits = (): string[] => {
    if (!hasCampingFilter) return [];

    // Get list of unit IDs where services includes camping service ids
    return visibleUnitObjects
      .filter((u) => {
        const containsService = u.services.includes(UnitServices.CAMPING);
        return containsService ? u : null;
      })
      .map((u) => u.id.toString());
  };

  const InformationPointUnits = (): string[] => {
    if (!hasInfoPointFilter) return [];

    // Get list of unit IDs where services includes information point service ids
    return visibleUnitObjects
      .filter((u) => {
        const containsService = u.services.includes(
          UnitServices.INFORMATION_POINT,
        );
        return containsService ? u : null;
      })
      .map((u) => u.id.toString());
  };

  const SkiLodgeUnits = (): string[] => {
    if (!hasSkiLodgeFilter) return [];

    // Get list of unit IDs where services includes ski lodge service ids
    return visibleUnitObjects
      .filter((u) => {
        const containsService = u.services.includes(UnitServices.SKI_LODGE);
        return containsService ? u : null;
      })
      .map((u) => u.id.toString());
  };
  // merge arrays into one and remove duplicate items from it
  const filteredUnits: string[] = union(
    skiTrackFreestyleUnits(),
    skiTrackTraditionalUnits(),
    skiTrackDogSkijoring(),
    leanToUnits(),
    CookingFacilityUnits(),
    CampingUnits(),
    InformationPointUnits(),
    SkiLodgeUnits(),
  );

  return filteredUnits;
};

export const handleSingleUnitConditionUpdate = (unit: Unit) => {
  const sport = getUnitSport(unit);
  const filters = [
    UnitFilters.COOKING_FACILITY,
    UnitFilters.CAMPING,
    UnitFilters.LEAN_TO,
    UnitFilters.INFORMATION_POINT,
    UnitFilters.SKI_LODGE,
    UnitFilters.ICE_SWIMMING,
  ] as string[];

  // Create a copy of the unit to avoid mutating frozen objects from RTK Query
  let updatedUnit = { ...unit };

  if (filters.includes(sport)) {
    const defaultObservations = [getDefaultUnitObservation(unit)];
    updatedUnit = { ...updatedUnit, observations: defaultObservations };
  }

  const { observations } = updatedUnit;

  // Safety check - ensure observations exists and is an array
  if (!observations || !Array.isArray(observations)) {
    return updatedUnit;
  }

  // Get latest primary observation
  const primaryObservation = observations.find((obs) => obs.primary) || null;

  const today = new Date();
  const lastObservation = primaryObservation 
    ? getObservationTime(primaryObservation) 
    : new Date(0); // Default to epoch if no observation
  const daysFromLastUpdate = differenceInDays(today, lastObservation);

  const getMinimumDaysToHandleConditionUpdates = (): number => {
    if (sport === "unknown") return 0;

    // Check if sport exists in UnitAutomaticConditionChangeDays
    if (!(sport in UnitAutomaticConditionChangeDays)) return 0;

    // Get satisfactory and unknown days based on sport
    const satisfactoryDays =
      UnitAutomaticConditionChangeDays[sport as keyof typeof UnitAutomaticConditionChangeDays].satisfactory;
    const unknownDays = UnitAutomaticConditionChangeDays[sport as keyof typeof UnitAutomaticConditionChangeDays].unknown;

    // Use satisfactory days if it exists
    if (satisfactoryDays !== undefined) {
      return satisfactoryDays;
    }

    // If unknownDays is undefined, return Infinity to disable automatic updates
    return unknownDays ?? Infinity;
  };

  // Don't do anything if:
  // Unit has been recently updated, OR
  // Unit is marked as "closed" OR
  // There's no primary observation available
  if (
    daysFromLastUpdate < getMinimumDaysToHandleConditionUpdates() ||
    primaryObservation?.value === "closed" ||
    !primaryObservation
  ) {
    return updatedUnit;
  }

  // Unknown
  // A copy of last primary observation,
  // where name and quality values are altered
  const unknownObservation = {
    ...primaryObservation,
    name: {
      fi: i18n.t("UNIT_DETAILS.UNKNOWN", { lng: "fi" }),
      sv: i18n.t("UNIT_DETAILS.UNKNOWN", { lng: "sv" }),
      en: i18n.t("UNIT_DETAILS.UNKNOWN", { lng: "en" }),
    },
    quality: UnitQualityConst.UNKNOWN,
  };

  // Satisfactory
  // A copy of last primary observation,
  // where name and quality values are altered
  const satisfactoryObservation = {
    ...primaryObservation,
    name: {
      fi: i18n.t("UNIT_DETAILS.SATISFACTORY", { lng: "fi" }),
      sv: i18n.t("UNIT_DETAILS.SATISFACTORY", { lng: "sv" }),
      en: i18n.t("UNIT_DETAILS.SATISFACTORY", { lng: "en" }),
    },
    quality: UnitQualityConst.SATISFACTORY,
  };

  // If a sport has satisfactory and unknown conditions, check if we need to use
  // satisfactory value for it (which should be a smaller number than unknown)
  const isSatisfactory: boolean =
    sport !== "unknown" &&
    sport in UnitAutomaticConditionChangeDays &&
    daysFromLastUpdate < (UnitAutomaticConditionChangeDays[sport as keyof typeof UnitAutomaticConditionChangeDays].unknown ?? Infinity);

  // Set new observation copy as the first observation in unit's observations list
  // Return a new unit object instead of mutating the original
  if (isSatisfactory) {
    return { ...updatedUnit, observations: [satisfactoryObservation, ...observations] };
  } else {
    return { ...updatedUnit, observations: [unknownObservation, ...observations] };
  }
};

export const handleUnitConditionUpdates = (units: Record<number, Unit>) => {
  return mapValues(units, (unit) => handleSingleUnitConditionUpdate(unit));
};

export const getDefaultUnitObservation = (unit: Unit) => {
  const sport = getUnitSport(unit);
  const observation = {
    primary: true,
    unit: unit.id,
    time: new Date().toISOString(),
    expiration_time: null,
    quality: UnitQualityConst.GOOD,
    value: UnitQualityConst.GOOD,
    id: 1400000 + Math.floor(Math.random() * 100000),
    property: [`${sport}_condition`],
    name: {
      fi: i18n.t("UNIT_DETAILS.SERVICE_CONDITION.DEFAULT", { lng: "fi" }),
      sv: i18n.t("UNIT_DETAILS.SERVICE_CONDITION.DEFAULT", { lng: "sv" }),
      en: i18n.t("UNIT_DETAILS.SERVICE_CONDITION.DEFAULT", { lng: "en" }),
    },
  };
  return observation;
};

export const calculateElevationStats = (
  geometry_3d: Unit["geometry_3d"],
): { highest: number; lowest: number } | null => {
  if (!geometry_3d) return null;
  const allElevations = geometry_3d.coordinates
    .flat()
    .map((pos) => pos[2])
    .filter((e): e is number => e !== undefined && !isNaN(e));
  if (allElevations.length === 0) return null;
  return {
    highest: Math.max(...allElevations),
    lowest: Math.min(...allElevations),
  };
};

export const getUnitObservations = (unit: Unit | undefined) => {
  if (!unit || !has(unit, "observations")) {
    return {
      liveTemperatureObservation: null,
      liveWaterQualityObservation: null,
      measuredTemperatureObservation: null,
      temperatureObservation: null,
      uirasSwimmingWaterTemperature: null,
    };
  }
  return {
    liveTemperatureObservation: getObservation(unit, "live_swimming_water_temperature"),
    liveWaterQualityObservation: getObservation(unit, "live_swimming_water_quality"),
    measuredTemperatureObservation: getObservation(unit, "measured_swimming_water_temperature"),
    temperatureObservation: getObservation(unit, "swimming_water_temperature"),
    uirasSwimmingWaterTemperature: getObservation(unit, "uiras_swimming_water_temperature"),
  };
};

export const isUnitInFavourites = (unit: Unit): boolean => {
  const favourites = JSON.parse(localStorage.getItem("favouriteUnits") ?? "[]") as Unit[];
  return favourites.some((favourite) => favourite.id === unit.id);
};

export const toggleFavourite = (unit: Unit): void => {
  const favourites = JSON.parse(localStorage.getItem("favouriteUnits") ?? "[]") as Unit[];
  const updated = favourites.some((f) => f.id === unit.id)
    ? favourites.filter((f) => f.id !== unit.id)
    : [...favourites, unit];
  localStorage.setItem("favouriteUnits", JSON.stringify(updated));
};
